// Angular `DatePipe`/`CurrencyPipe`/`DecimalPipe` equivalents for the formats the migrated views use. Kept in one place because
// the offering components alone need four of them and every ad-hoc `toLocaleDateString` call is one
// more chance to drift from the Angular rendering.
import { i18n } from '@shared/i18n';

// Angular's pipes format against `LOCALE_ID`, which follows the `/app/<locale>/…` prefix. The
// vue-i18n locale is the same source of truth; it only needs the region the Intl APIs expect.
const REGION_BY_LOCALE: Record<string, string> = {
    de: 'de-DE',
    en: 'en-GB',
    es: 'es-ES',
    fr: 'fr-FR',
    hu: 'hu-HU',
    it: 'it-IT',
    pl: 'pl-PL',
    ro: 'ro-RO',
    ru: 'ru-RU',
    tr: 'tr-TR',
    uk: 'uk-UA',
};

export function activeLocale(): string {
    return REGION_BY_LOCALE[i18n.global.locale.value] ?? 'de-DE';
}

const DATE_OPTS: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: '2-digit' };
const TIME_OPTS: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };

/** Angular `date: 'shortDate'` → `07.09.26` */
export function formatShortDate(value: string | Date | null | undefined, locale = activeLocale()): string {
    if (!value) {
        return '';
    }
    return new Date(value).toLocaleDateString(locale, DATE_OPTS);
}

/** Angular `date: 'shortTime'` → `14:00` */
export function formatShortTime(value: string | Date | null | undefined, locale = activeLocale()): string {
    if (!value) {
        return '';
    }
    return new Date(value).toLocaleTimeString(locale, TIME_OPTS);
}

/** Angular `date` without a format argument (`mediumDate`) → `01.02.1990` */
export function formatMediumDate(value: string | Date | null | undefined, locale = activeLocale()): string {
    if (!value) {
        return '';
    }
    return new Date(value).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Angular `date: 'short'` → `07.09.26, 14:00` */
export function formatShort(value: string | Date | null | undefined, locale = activeLocale()): string {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    return `${date.toLocaleDateString(locale, DATE_OPTS)}, ${date.toLocaleTimeString(locale, TIME_OPTS)}`;
}

/**
 * Angular `date: 'E'` → `Mo.`
 *
 * `@angular/common`'s German locale data still carries the abbreviation dot that current CLDR (and
 * therefore `Intl`) dropped, so it is re-added for German to keep the rendering identical.
 */
export function formatWeekdayShort(value: string | Date | null | undefined, locale = activeLocale()): string {
    if (!value) {
        return '';
    }
    const weekday = new Date(value).toLocaleDateString(locale, { weekday: 'short' });
    if (locale.startsWith('de') && !weekday.endsWith('.')) {
        return `${weekday}.`;
    }
    return weekday;
}

/**
 * Angular `date: 'ccc'` → `Mo`
 *
 * The *standalone* abbreviation, which German spells without the trailing dot that the `E` form
 * carries — `Intl` returns exactly that when `weekday` is the only requested field.
 */
export function formatWeekdayShortStandalone(value: string | Date | null | undefined, locale = activeLocale()): string {
    if (!value) {
        return '';
    }
    return new Date(value).toLocaleDateString(locale, { weekday: 'short' });
}

/** Angular `number`/`currency` pipe with `'1.2-2'` → `20,00 €` */
export function formatCurrency(value: number, currency = 'EUR', locale = activeLocale()): string {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
}

/** Angular `number` pipe (default digits info `1.0-3`) */
export function formatDecimal(value: number, locale = activeLocale()): string {
    return new Intl.NumberFormat(locale, { minimumFractionDigits: 0, maximumFractionDigits: 3 }).format(value);
}
